import type { ReactNode } from 'react'
import { mergeClassNames } from '@/design-system/lib/mergeClassNames'

export type ListCellTone = 'muted' | 'danger' | 'warning' | 'success' | 'info'

export interface ListCellProps {
  /** Niveau 1 : nom, montant, libellé. */
  primary?: ReactNode
  /** Niveau 2 : identifiant, ville, précision. */
  secondary?: ReactNode
  /** Le niveau 1 porte l'identité de la ligne (650). */
  strong?: boolean
  /** Ton du niveau 2; atténué par défaut. Toujours accompagné d'un libellé. */
  secondaryTone?: ListCellTone
  /** Repère de couleur avant le niveau 1 (route). Décoratif : le libellé suit toujours. */
  swatch?: string | null | undefined
  /** Étiquette discrète à côté du niveau 1 (VIP, Identité à compléter). */
  tag?: ReactNode
  align?: 'start' | 'end'
  /** Dates, heures et montants courts : jamais coupés au milieu (« 09 h 42 »). */
  nowrap?: boolean
  className?: string
}

/**
 * Cellule à deux niveaux visuels au plus. Le texte revient à la ligne plutôt que d'être tronqué :
 * un nom, une adresse ou un identifiant ne disparaissent pas derrière une ellipse.
 */
export function ListCell({
  primary,
  secondary,
  strong = false,
  secondaryTone = 'muted',
  swatch,
  tag,
  align = 'start',
  nowrap = false,
  className,
}: ListCellProps) {
  const hasPrimary = primary !== undefined && primary !== null && primary !== ''
  const hasSecondary = secondary !== undefined && secondary !== null && secondary !== ''
  return (
    <span
      className={mergeClassNames(
        'ds-list-cell',
        align === 'end' && 'ds-list-cell--end',
        nowrap && 'ds-list-cell--nowrap',
        className,
      )}
    >
      {hasPrimary || tag ? (
        <span className={mergeClassNames('ds-list-cell__primary', strong && 'ds-list-cell__primary--strong')}>
          {swatch !== undefined ? (
            <span
              className="ds-list-cell__swatch"
              style={swatch ? { background: swatch } : undefined}
              aria-hidden="true"
            />
          ) : null}
          {hasPrimary ? <span>{primary}</span> : null}
          {tag}
        </span>
      ) : null}
      {hasSecondary ? (
        <span className={`ds-list-cell__secondary ds-list-cell__secondary--${secondaryTone}`}>
          {secondary}
        </span>
      ) : null}
    </span>
  )
}
